import { useApi, SKY_API_ENDPOINT } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import {
  CurrencyValue,
  Flex,
  formatDatetime,
  formatNumber,
  LineChart
} from '@blockanalitica/ui';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useCallback, useMemo, useState } from 'react';

export default function ASCGraph() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [response, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: `/asc/history/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });
  const data = response?.data;

  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const config = updateChartConfigWithPeriod({
    options: {
      scales: {
        y: {
          stacked: true
        }
      }
    } as ExtendedChartOptions
  });

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const yScaleValueFormatter = useMemo(
    () => (value: number) => {
      return `${formatNumber(value)}`;
    },
    []
  );

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      return (
        <CurrencyValue
          value={value.toString()}
          options={{
            currency: 'USD'
          }}
        />
      );
    };
  }, []);

  const datasets = useMemo(() => {
    return [
      {
        label: 'Resting ASC',
        xField: 'date',
        yField: 'total_resting_asc',
        borderColor: 'rgba(14, 198, 211, 0.8)',
        backgroundColor: 'rgba(14, 198, 211, 0.8)'
      },
      {
        label: 'Latent ASC',
        xField: 'date',
        yField: 'total_latent_asc',
        borderColor: 'rgba(77, 124, 234, 0.8)',
        backgroundColor: 'rgba(77, 124, 234, 0.8)'
      }
    ];
  }, []);
  if (error) {
    throw error;
  }
  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <ChartSkeleton />
      </Flex>
    );
  }

  return (
    <LineChart
      data={data}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      config={config}
      subValueFormatter={subValueFormatter}
      showKpisAsTooltip={true}
      tooltip={{
        labelFormatter: (value) => `${value}`,
        titleFormatter: (_value, tooltipItems) => {
          const dateValue =
            tooltipItems[0]?.raw &&
            typeof tooltipItems[0].raw === 'object' &&
            'x' in tooltipItems[0].raw
              ? (tooltipItems[0].raw as { x?: string | number })?.x
              : tooltipItems[0]?.parsed &&
                  typeof tooltipItems[0].parsed === 'object' &&
                  'x' in tooltipItems[0].parsed
                ? (tooltipItems[0].parsed as { x?: string | number })?.x
                : undefined;
          return `Date: ${formatDatetime(dateValue, { format: 'MMM dd, yyyy' })}`;
        },
        valueFormatter: (value) => `$${formatNumber(Number(value))}`
      }}
    />
  );
}
